/**
 * The governed service-category catalog (W9-CATEGORY-CATALOG, D462, ADR-0080; DevMobility B-001, A-011, B-002, B-010,
 * B-018, B-021, B-049, I-100).
 *
 * Before D462 a category existed only implicitly: publishing a tariff for `(market, code)` made the rider app list it,
 * named by an app-bundled message or its code in capitals, with no icon, order, message, status or feature flags, and
 * the rich `ServiceCategory` kernel was imported by no service. This module is the catalog an operator maintains:
 *
 * - **Entry.** One category's presentation and behaviour flags: `kind` (the aggregate it drives), the i18n `nameKey`
 *   plus per-language `labels` (the fallback when an app bundle lacks the key — an operator-created category has none),
 *   an `iconToken` from the design-system vocabulary, the `order` in the rider's list, an optional `message`, the
 *   physical `vehicleClasses` (the same vocabulary as D437's eligibility data; `bike` is the non-motorized class) and the
 *   `features` the apps read (`category.features`, B-021).
 * - **Scope.** An entry is published at `platform`, `country` (`XX`), administrative `region` (`XX-YYY`, a grouping of
 *   markets — B-010) or `market` (`xx:slug`). For a market, the MOST SPECIFIC scope with an entry in force wins, whole:
 *   market → region → country → platform (ADR-0005 resolution order). A scope above may LOCK features (`lockedFeatures`):
 *   no lower scope can switch them on, and resolution masks them even on an entry approved before the lock — a lower
 *   scope never loosens a higher one.
 * - **Versions.** A change is a new version of one bucket `(scope, scopeRef, code)` under four eyes (the store). The
 *   version in force at an instant is the bucket's highest approved version whose `effectiveFrom` is not after it.
 * - **Status without a version (B-049).** Deactivating a category at a scope is a separate, audited switch — no new
 *   version: it applies at once and to every market below that scope. Reactivating is a four-eyes request (it re-offers
 *   the category). A deactivation also covers a category that has no catalog entry yet (a fare-only category).
 *
 * Per-category governance: a category with NO entry in a market's ancestry keeps the pre-D462 behaviour (offered when
 * its tariff is), so publishing one entry never makes the other categories of a market vanish.
 *
 * Pure and deterministic. Neutral: codes, scopes, languages, icons and vehicle classes are data; no country is named.
 */
import { createHash } from 'node:crypto';
import { canonicalPolicyValue } from '@vello/config';
import { AGGREGATE_KINDS, VEHICLE_CLASSES } from './category.js';
// ---- scopes ---------------------------------------------------------------------------------------------------------
/** The scopes a catalog entry (or a deactivation) may be published at, most general first (ADR-0005). */
export const CATALOG_SCOPES = ['platform', 'country', 'region', 'market'];
const COUNTRY_REF = /^[A-Z]{2}$/;
/** An administrative region (ISO 3166-2 subdivision, the `markets.region_code` vocabulary). */
const REGION_REF = /^[A-Z]{2}-[A-Z0-9]{1,3}$/;
const MARKET_REF = /^[a-z]{2}:[a-z0-9]+(?:-[a-z0-9]+)*$/;
/** Whether a target is well formed (the database CHECK repeats it). */
export function isCatalogTarget(target) {
    if (typeof target.scopeRef !== 'string')
        return false;
    switch (target.scope) {
        case 'platform':
            return target.scopeRef === '';
        case 'country':
            return COUNTRY_REF.test(target.scopeRef);
        case 'region':
            return REGION_REF.test(target.scopeRef);
        case 'market':
            return MARKET_REF.test(target.scopeRef);
        default:
            return false;
    }
}
/**
 * The market's administrative region when it lies inside the market's OWN country (an ISO 3166-2 code starts with its
 * country's alpha-2). A region of another country is never an ancestor: the registry does not cross-check it, and a
 * country's operators must not rule another country's market through a region (review W9A-01).
 */
export function marketRegionOf(market) {
    const r = market.regionCode;
    return r !== undefined && r !== '' && r.slice(0, 2).toUpperCase() === market.countryCode.toUpperCase() ? r : undefined;
}
/** A market's buckets, most specific first: market → region (when it has one) → country → platform. */
export function ancestryTargets(market) {
    const region = marketRegionOf(market);
    return [
        { scope: 'market', scopeRef: market.marketId },
        ...(region !== undefined ? [{ scope: 'region', scopeRef: region }] : []),
        { scope: 'country', scopeRef: market.countryCode },
        { scope: 'platform', scopeRef: '' },
    ];
}
/** Whether `target` is the market itself or one of its ancestors. */
export function targetCoversMarket(target, market) {
    return ancestryTargets(market).some((t) => t.scope === target.scope && t.scopeRef === target.scopeRef);
}
const rank = (scope) => CATALOG_SCOPES.indexOf(scope);
// ---- vehicle classes (composes with D437) ---------------------------------------------------------------------------
/**
 * The physical classes that have no motor — the class D437's courier rule exempts from the inspection and the vehicle
 * documents (owner answer, PR 291 question 5). Every other class of `VEHICLE_CLASSES` is motorized.
 */
export const NON_MOTORIZED_VEHICLE_CLASSES = ['bike'];
export function isMotorizedVehicleClass(vehicleClass) {
    return !NON_MOTORIZED_VEHICLE_CLASSES.includes(vehicleClass);
}
// ---- features (B-021) -----------------------------------------------------------------------------------------------
/**
 * The flags the server resolves and delivers to the apps as `category.features` (B-021): the kernel's capabilities plus
 * the app flags of the DevMobility "Serviço por Região" (tips, the driver switching the category off, the message pop-up).
 * A flag says the category ALLOWS the feature; the feature's own policy (negotiation, reservation, …) still decides.
 */
export const CATEGORY_FEATURES = [
    'scheduling',
    'negotiation',
    'dynamicPricing',
    'pet',
    'extraBaggage',
    'accessibility',
    'matchingPriority',
    'thirdPartyRide',
    'proofOfDelivery',
    'tips',
    'driverMayOptOut',
    'messagePopup',
];
/** Every feature off; a partial map switches on only what it names. */
export function categoryFeatures(partial) {
    const out = {};
    for (const f of CATEGORY_FEATURES)
        out[f] = partial?.[f] === true;
    return out;
}
export const CATALOG_REASON_MAX = 500;
export const CATALOG_MAX_ORDER = 9999;
/** D705: the most seats a category may state (a bus-sized vehicle is not a ride category). */
export const CATALOG_MAX_SEATS = 60;
const CODE = /^[a-z][a-z0-9_]{0,31}$/;
const NAME_KEY = /^[a-z][A-Za-z0-9_.-]{0,95}$/;
const ICON_TOKEN = /^[a-z][a-z0-9-]{0,31}$/;
const LANGUAGE_TAG = /^[a-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const LABEL_MAX = 60;
const MESSAGE_MAX = 280;
const MAX_LABELS = 12;
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const ENTRY_FIELDS = new Set(['code', 'kind', 'nameKey', 'labels', 'iconToken', 'order', 'message', 'vehicleClasses', 'seats', 'features', 'lockedFeatures']);
function parseLabels(raw, max, path, problems, requiredCode) {
    if (!isObj(raw) || Object.keys(raw).length === 0) {
        problems.push({ code: requiredCode, path });
        return undefined;
    }
    const out = {};
    const keys = Object.keys(raw).sort();
    if (keys.length > MAX_LABELS)
        problems.push({ code: 'LABEL_INVALID', path });
    for (const lang of keys) {
        const text = raw[lang];
        if (!LANGUAGE_TAG.test(lang) || typeof text !== 'string' || text.trim() === '' || text.trim().length > max) {
            problems.push({ code: requiredCode === 'MESSAGE_INVALID' ? 'MESSAGE_INVALID' : 'LABEL_INVALID', path: `${path}.${lang}` });
            continue;
        }
        out[lang] = text.trim();
    }
    return out;
}
/**
 * Validate and normalize a candidate entry for a target (a DRAFT): the shape, the vocabularies, and the target's own
 * rules (a market carries no locks). The ancestor locks are checked separately ({@link lockedByAncestors}), at the
 * proposal AND again at the approval. Returns the normalized entry (trimmed texts, sorted labels, full feature map,
 * sorted locks) or every problem at once.
 */
export function parseCatalogEntry(raw, target, expectedCode) {
    if (!isObj(raw))
        return { ok: false, problems: [{ code: 'ENTRY_NOT_OBJECT', path: '' }] };
    const problems = [];
    for (const k of Object.keys(raw))
        if (!ENTRY_FIELDS.has(k))
            problems.push({ code: 'UNKNOWN_FIELD', path: k });
    const code = typeof raw.code === 'string' ? raw.code : '';
    if (!CODE.test(code))
        problems.push({ code: 'CODE_INVALID', path: 'code' });
    else if (expectedCode !== undefined && code !== expectedCode)
        problems.push({ code: 'CODE_MISMATCH', path: 'code' });
    const kind = raw.kind;
    if (!AGGREGATE_KINDS.includes(kind))
        problems.push({ code: 'KIND_INVALID', path: 'kind' });
    const nameKey = typeof raw.nameKey === 'string' ? raw.nameKey.trim() : '';
    if (!NAME_KEY.test(nameKey))
        problems.push({ code: 'NAME_KEY_INVALID', path: 'nameKey' });
    const labels = parseLabels(raw.labels, LABEL_MAX, 'labels', problems, 'LABELS_REQUIRED');
    const iconToken = typeof raw.iconToken === 'string' ? raw.iconToken : '';
    if (!ICON_TOKEN.test(iconToken))
        problems.push({ code: 'ICON_TOKEN_INVALID', path: 'iconToken' });
    const order = raw.order;
    if (typeof order !== 'number' || !Number.isInteger(order) || order < 0 || order > CATALOG_MAX_ORDER)
        problems.push({ code: 'ORDER_INVALID', path: 'order' });
    let message;
    if (raw.message !== undefined && raw.message !== null) {
        if (!isObj(raw.message) || Object.keys(raw.message).some((k) => k !== 'labels'))
            problems.push({ code: 'MESSAGE_INVALID', path: 'message' });
        else {
            const m = parseLabels(raw.message.labels, MESSAGE_MAX, 'message.labels', problems, 'MESSAGE_INVALID');
            if (m !== undefined)
                message = { labels: m };
        }
    }
    const vehicleClasses = [];
    if (!Array.isArray(raw.vehicleClasses) || raw.vehicleClasses.length === 0)
        problems.push({ code: 'VEHICLE_CLASS_REQUIRED', path: 'vehicleClasses' });
    else {
        raw.vehicleClasses.forEach((v, i) => {
            if (!VEHICLE_CLASSES.includes(v))
                problems.push({ code: 'VEHICLE_CLASS_UNKNOWN', path: `vehicleClasses[${i}]` });
            else if (vehicleClasses.includes(v))
                problems.push({ code: 'VEHICLE_CLASS_DUPLICATE', path: `vehicleClasses[${i}]` });
            else
                vehicleClasses.push(v);
        });
    }
    // D705 (G7): the seats are optional; a stated one is a whole number in range.
    const seats = raw.seats;
    if (seats !== undefined && seats !== null && (typeof seats !== 'number' || !Number.isInteger(seats) || seats < 1 || seats > CATALOG_MAX_SEATS))
        problems.push({ code: 'SEATS_INVALID', path: 'seats' });
    let features = categoryFeatures();
    if (raw.features !== undefined) {
        if (!isObj(raw.features))
            problems.push({ code: 'FEATURES_INVALID', path: 'features' });
        else {
            const partial = {};
            for (const [k, v] of Object.entries(raw.features)) {
                if (!CATEGORY_FEATURES.includes(k))
                    problems.push({ code: 'FEATURE_UNKNOWN', path: `features.${k}` });
                else if (typeof v !== 'boolean')
                    problems.push({ code: 'FEATURES_INVALID', path: `features.${k}` });
                else
                    partial[k] = v;
            }
            features = categoryFeatures(partial);
        }
    }
    if (kind === 'mobility' && features.proofOfDelivery)
        problems.push({ code: 'PROOF_OF_DELIVERY_ON_MOBILITY', path: 'features.proofOfDelivery' });
    if (features.messagePopup && message === undefined)
        problems.push({ code: 'MESSAGE_POPUP_WITHOUT_MESSAGE', path: 'features.messagePopup' });
    let lockedFeatures;
    if (raw.lockedFeatures !== undefined) {
        if (!Array.isArray(raw.lockedFeatures))
            problems.push({ code: 'LOCKED_FEATURE_UNKNOWN', path: 'lockedFeatures' });
        else {
            const set = new Set();
            raw.lockedFeatures.forEach((f, i) => {
                if (!CATEGORY_FEATURES.includes(f))
                    problems.push({ code: 'LOCKED_FEATURE_UNKNOWN', path: `lockedFeatures[${i}]` });
                else
                    set.add(f);
            });
            if (set.size > 0)
                lockedFeatures = [...set].sort();
            if (set.size > 0 && target.scope === 'market')
                problems.push({ code: 'LOCKED_FEATURES_AT_MARKET', path: 'lockedFeatures' });
            // A lock keeps a feature OFF below (a lower scope never loosens it): the locking entry has it off itself.
            for (const f of set)
                if (features[f])
                    problems.push({ code: 'LOCKED_FEATURE_ENABLED', path: `lockedFeatures.${f}` });
        }
    }
    if (problems.length > 0 || labels === undefined)
        return { ok: false, problems };
    return {
        ok: true,
        entry: {
            code,
            kind,
            nameKey,
            labels,
            iconToken,
            order: order,
            ...(message !== undefined ? { message } : {}),
            vehicleClasses,
            ...(typeof seats === 'number' ? { seats } : {}),
            features,
            ...(lockedFeatures !== undefined ? { lockedFeatures } : {}),
        },
    };
}
/** SHA-256 of the canonical entry: binds a proposal, its approval and every snapshot to the exact content. */
export function hashCatalogEntry(entry) {
    return createHash('sha256').update(canonicalPolicyValue(entry)).digest('hex');
}
/** The version of a bucket in force at `at`: its highest approved version whose start is not after it. */
export function effectiveCatalogVersion(versions, target, code, at) {
    const t = Date.parse(at);
    let winner;
    for (const v of versions) {
        if (v.scope !== target.scope || v.scopeRef !== target.scopeRef || v.code !== code)
            continue;
        if (Date.parse(v.effectiveFrom) > t)
            continue;
        if (winner === undefined || v.version > winner.version)
            winner = v;
    }
    return winner;
}
/**
 * The features the scopes ABOVE `target` lock for `code` at `at` (the union of their in-force versions' locks). A
 * proposal at `target` may not switch one on, and resolution masks them.
 */
export function lockedByAncestors(versions, target, code, at, market) {
    const above = [];
    if (market !== undefined) {
        for (const t of ancestryTargets(market))
            if (rank(t.scope) < rank(target.scope))
                above.push(t);
    }
    else {
        // Without a market (a country or region proposal), the ancestors are the platform and, for a region, its country.
        if (rank(target.scope) > rank('platform'))
            above.push({ scope: 'platform', scopeRef: '' });
        if (target.scope === 'region')
            above.push({ scope: 'country', scopeRef: target.scopeRef.slice(0, 2) });
        if (target.scope === 'market')
            above.push({ scope: 'country', scopeRef: target.scopeRef.slice(0, 2).toUpperCase() });
    }
    const locked = new Set();
    for (const t of above)
        for (const f of effectiveCatalogVersion(versions, t, code, at)?.entry.lockedFeatures ?? [])
            locked.add(f);
    return [...locked].sort();
}
/** The locked features an entry would switch on (empty = none): a proposal with any is refused `FEATURE_LOCKED_BY_ANCESTOR`. */
export function lockViolations(entry, locked) {
    return locked.filter((f) => entry.features[f]).map((f) => ({ code: 'FEATURE_LOCKED_BY_ANCESTOR', path: `features.${f}` }));
}
// ---- resolution for a market ----------------------------------------------------------------------------------------
/** The stable reasons a category is (not) offered in a market. */
export const CATEGORY_OFFER_REASONS = {
    OFFERED: 'CATEGORY_OFFERED',
    INACTIVE: 'CATEGORY_INACTIVE',
    NOT_IN_CATALOG: 'CATEGORY_CATALOG_UNSET',
};
function deactivationFor(deactivations, market, code) {
    // The most specific deactivation wins the explanation; any one hides the category.
    for (const t of ancestryTargets(market)) {
        const d = deactivations.find((x) => x.code === code && x.scope === t.scope && x.scopeRef === t.scopeRef);
        if (d !== undefined)
            return d;
    }
    return undefined;
}
/**
 * The catalog of a market at `at`: for every code with an entry in force somewhere in the market's ancestry, the most
 * specific one, its features masked by every lock above the winning scope, and its status (INACTIVE when a deactivation
 * covers the market). Sorted by `order`, then code.
 */
export function resolveMarketCatalog(input) {
    const targets = ancestryTargets(input.market);
    const codes = new Set();
    for (const v of input.versions)
        if (targets.some((t) => t.scope === v.scope && t.scopeRef === v.scopeRef))
            codes.add(v.code);
    const out = [];
    for (const code of codes) {
        let winner;
        for (const t of targets) {
            winner = effectiveCatalogVersion(input.versions, t, code, input.at);
            if (winner !== undefined)
                break;
        }
        if (winner === undefined)
            continue; // only future-dated versions so far
        const locked = new Set(lockedByAncestors(input.versions, winner, code, input.at, input.market));
        const features = {};
        for (const f of CATEGORY_FEATURES)
            features[f] = winner.entry.features[f] === true && !locked.has(f);
        const off = deactivationFor(input.deactivations, input.market, code);
        const e = winner.entry;
        out.push({
            code,
            kind: e.kind,
            nameKey: e.nameKey,
            labels: e.labels,
            iconToken: e.iconToken,
            order: e.order,
            ...(e.message !== undefined ? { message: e.message } : {}),
            vehicleClasses: e.vehicleClasses,
            motorized: e.vehicleClasses.some(isMotorizedVehicleClass),
            ...(e.seats !== undefined ? { seats: e.seats } : {}),
            features,
            source: { code, scope: winner.scope, scopeRef: winner.scopeRef, version: winner.version, contentHash: winner.contentHash },
            status: off === undefined ? 'ACTIVE' : 'INACTIVE',
            ...(off !== undefined ? { inactiveAt: { scope: off.scope, scopeRef: off.scopeRef } } : {}),
            reasonCode: off === undefined ? CATEGORY_OFFER_REASONS.OFFERED : CATEGORY_OFFER_REASONS.INACTIVE,
        });
    }
    return out.sort((a, b) => a.order - b.order || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));
}
export function marketCatalogView(input) {
    const targets = ancestryTargets(input.market);
    const inactive = new Set(input.deactivations.filter((d) => targets.some((t) => t.scope === d.scope && t.scopeRef === d.scopeRef)).map((d) => d.code));
    return { categories: resolveMarketCatalog(input), inactiveCodes: [...inactive].sort() };
}
/**
 * The quote and request gate. A deactivation covering the market refuses the category (`CATEGORY_INACTIVE`) whether or
 * not the catalog has an entry for it; an entry in force offers it (`CATEGORY_OFFERED`, with the snapshot); a category
 * with neither keeps the pre-D462 behaviour (`CATEGORY_CATALOG_UNSET`, offered — its tariff decides).
 */
export function decideCategoryOffer(input) {
    const off = deactivationFor(input.deactivations, input.market, input.code);
    if (off !== undefined)
        return { offered: false, reasonCode: CATEGORY_OFFER_REASONS.INACTIVE, inactiveAt: { scope: off.scope, scopeRef: off.scopeRef } };
    const category = resolveMarketCatalog({ ...input }).find((c) => c.code === input.code);
    if (category === undefined)
        return { offered: true, reasonCode: CATEGORY_OFFER_REASONS.NOT_IN_CATALOG };
    return { offered: true, reasonCode: CATEGORY_OFFER_REASONS.OFFERED, category };
}
/** The label for a language: the exact tag, then its primary subtag, then any label (stable: the first by tag). */
export function catalogLabelFor(labels, language) {
    if (labels[language] !== undefined)
        return labels[language];
    const primary = language.split('-')[0];
    if (labels[primary] !== undefined)
        return labels[primary];
    const tags = Object.keys(labels).sort();
    return tags.length > 0 ? labels[tags[0]] : undefined;
}
//# sourceMappingURL=catalog.js.map